import { useState } from "react";
import { Link } from 'react-router-dom';
import usePageTitle from "../hooks/usePageTitle";
import StandardLink from "../tools/StandardLink";
import DemoGrid from "../components/exploration/DemoGrid";
import SpotlightLayer from "../components/exploration/SpotlightLayer";

import "gridstack/dist/gridstack.min.css";
import "../styles/exploration.scss";

export default function Exploration() {
  usePageTitle("Exploration | Dochano");
  const [activeDemo, setActiveDemo] = useState(null);
  const [flippingCard, setFlippingCard] = useState(null);

  return (
    <section id="Exploration">
      <div id="Intro">
        <h1>Exploration</h1>
      </div>
      <p>
        This page is devoted to a test arena along with interesting sites
        related to Web Engineering.
      </p>

      <div id="css-tricks">
        <h2>CSS Tricks</h2>
        <p className="indent">
          I keep up with some site styling topics. Back in my University days as
          a computer lab tech, I would challenge myself to make any design or
          picture into a working website. This area is dedicated to fun elements
          that can make a site stand out. Inspired by{" "}
          <StandardLink href="https://codepen.io/spark" isNewWindow={true}>
            CodePen Spark
          </StandardLink>{" "}
          and{" "}
          <StandardLink href="https://css-tricks.com/" isNewWindow={true}>
            CSS Tricks
          </StandardLink>
        </p>

        <DemoGrid
          activeDemo={activeDemo}
          setActiveDemo={setActiveDemo}
          flippingCard={flippingCard}
          setFlippingCard={setFlippingCard}
        />

        {activeDemo && (
          <SpotlightLayer
            activeDemo={activeDemo}
            setActiveDemo={setActiveDemo}
            setFlippingCard={setFlippingCard}
          />
        )}

        <dl className="indent">
          <dt>
            <StandardLink href="https://dev.to/ivordev/triggering-a-popover-with-pure-css-no-javascript-required-12eb" isNewWindow={true}>
              CSS Only Pop-up
            </StandardLink>
          </dt>
          <dd>
            Uses input checkbox style trigger handling to open. Interesting but
            could be odd for A11Y users.
          </dd>
        </dl>
      </div>

      <div id="python-playground">
        <h2>Python Playground</h2>
        <p className="indent">
          Be sure to checkout my public{" "}
          <StandardLink href="https://github.com/grjohnson1/Python_Arena" isNewWindow={true}>
            GitHub python_arena
          </StandardLink>{" "}
          to see projects I’ve been working on in my spare time. Originally I
          dove into Machine Learning, Model Training, and AI. Now I am diving
          deeper into Python coding.
        </p>

        <h3>Python Standout Scripts</h3>

        <h4>mazeGenerator.py</h4>
        <p className="indent">
          Draws a maze using a grid layout. This inspired me to create a maze
          game for my website: <Link href="/Maze">Maze</Link>.
        </p>

        <h4>Dolittle.py</h4>
        <p className="indent">
          A learning script that asks questions to identify an animal you’re
          thinking of. If it fails, it asks for the animal and a unique
          question, storing it for future attempts. Over time, it learns
          iteratively — showing how knowledge can grow without a massive data
          lake.
        </p>

        <h4>pdf_keyword_search.py</h4>
        <p className="indent">
          Searches PDFs in a folder for given keywords and generates a CSV with
          results. This was personally created outside of textbook examples.
        </p>

        <h4>
          <StandardLink href="https://github.com/grjohnson1/Python_Arena/tree/main/AI_InEasySteps/FlatworldGame" isNewWindow={true}>
            Flatworld Game
          </StandardLink>
        </h4>
        <p className="indent">
          Visual game where projects build off each other. Best to see
          screenshots and read more from the{" "}
          <StandardLink href="https://github.com/grjohnson1/Python_Arena/tree/main/AI_InEasySteps/FlatworldGame" isNewWindow={true}>
            GitHub Repo Readme file
          </StandardLink>{" "}
          directly.
        </p>
      </div>
    </section>
  );
}
